import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardActionArea, CardContent,
  Button, Chip, Skeleton, Avatar, Paper,
  Stack, useTheme, useMediaQuery, Badge, CircularProgress
} from '@mui/material';
import {
  Explore as ExploreIcon,
  MenuBook as MenuBookIcon,
  ArrowForward as ArrowForwardIcon,
  AutoStories as PathsIcon,
  Book as JournalIcon,
  Chat as ChatIcon,
  Add as AddIcon,
  TrendingUp as ProgressIcon,
  LocalFireDepartment as StreakIcon,
  Map as MapIcon,
  School as LearnIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import { useLanguage } from '../context/LanguageContext';
// Single source of truth for language names/flags (covers es-419, pt-BR, etc.)
import { getLanguageDisplayName } from '../config/languagesConfig';

const HomePage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const { selectedLanguage } = useLanguage();

  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  // One request: GET /api/progress carries the stats AND the recent
  // conversations / paths / journal entries (it used to take five calls in a row).
  useEffect(() => {
    let cancelled = false;
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const res = await apiClient.progress.getProgress(selectedLanguage);
        if (!cancelled) setProgress(res.data || null);
      } catch (e) {
        console.error('progress', e);
        if (!cancelled) setProgress(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (user && selectedLanguage) fetchUserData();
    return () => { cancelled = true; };
  }, [user, selectedLanguage]);

  const recent = progress?.recent || {};
  const recentConversations = recent.conversations || [];
  const recentPaths = recent.paths || [];
  const recentJournal = recent.journal || [];

  // Vocabulary headline
  const totalVocabWords = progress?.vocabulary?.total || 0;
  const wordsLearned = progress?.vocabulary?.level_3 || 0;

  // Quick actions — the four "do" verbs, names identical to the navigation.
  const quickActions = [
    { title: 'Start', description: 'New conversation or story', icon: <ExploreIcon />, color: 'primary.main', action: () => navigate('/explore') },
    { title: 'Voyages', description: 'Story paths', icon: <PathsIcon />, color: 'secondary.main', action: () => navigate('/breadcrumbs'), badge: recentPaths.length > 0 ? 'New' : null },
    { title: 'Words', description: `${totalVocabWords} words`, icon: <MenuBookIcon />, color: 'success.main', action: () => navigate('/vocabulary'), badge: totalVocabWords > 0 ? totalVocabWords : null },
    { title: 'Journal', description: 'Write & reflect', icon: <JournalIcon />, color: 'warning.main', action: () => navigate('/journal') },
  ];

  // Reusable "section header with View all" row.
  const SectionHeader = ({ title, onViewAll }) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, mt: 3 }}>
      <Typography variant="h6" sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600 }}>
        {title}
      </Typography>
      {onViewAll && (
        <Button size="small" endIcon={<ArrowForwardIcon />} onClick={onViewAll}>View all</Button>
      )}
    </Stack>
  );

  const EmptyRow = ({ text, cta, onClick, icon }) => (
    <Paper variant="outlined" sx={{ p: 2.5, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed' }}>
      {icon}
      <Typography variant="body2" color="text.secondary" sx={{ mb: cta ? 1.5 : 0, mt: icon ? 1 : 0 }}>{text}</Typography>
      {cta && <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onClick}>{cta}</Button>}
    </Paper>
  );

  const p = progress || {};
  const pv = p.vocabulary || {};
  const ps = p.stories || {};
  const pa = p.activity || {};

  return (
    <Box sx={{ pt: '12px', pb: isMobile ? '80px' : '40px' }}>
      {/* Welcome banner — on-theme navy/gold */}
      <Paper
        elevation={0}
        sx={{
          background:
            'radial-gradient(900px 320px at 85% -20%, rgba(198,152,43,0.28), transparent 60%),' +
            'linear-gradient(160deg, #0A3161 0%, #072347 100%)',
          backgroundColor: '#0A3161',
          color: 'white',
          p: isMobile ? 2.5 : 3.5,
          mb: 3,
          borderRadius: 3,
          border: '1px solid rgba(198,152,43,0.35)',
        }}
      >
        <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600 }}>
          Welcome back, {user?.first_name}
        </Typography>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip icon={<LearnIcon />} label={getLanguageDisplayName(selectedLanguage)}
            sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white', '& .MuiChip-icon': { color: 'white' } }} />
          {wordsLearned > 0 && (
            <Chip icon={<ProgressIcon />} label={`${wordsLearned} words mastered`}
              sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white', '& .MuiChip-icon': { color: 'white' } }} />
          )}
        </Stack>
      </Paper>

      {/* Quick actions */}
      <Grid container spacing={2}>
        {quickActions.map((action, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card elevation={2} sx={{ height: '100%', transition: 'all 0.25s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 5 } }}>
              <CardActionArea onClick={action.action} sx={{ height: '100%', p: isMobile ? 1.5 : 2 }}>
                <Stack spacing={1} alignItems="center" textAlign="center">
                  <Badge badgeContent={action.badge} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}>
                    <Avatar sx={{ bgcolor: action.color, width: isMobile ? 40 : 56, height: isMobile ? 40 : 56 }}>
                      {action.icon}
                    </Avatar>
                  </Badge>
                  <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={600}>{action.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{action.description}</Typography>
                </Stack>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Recent conversations */}
      <SectionHeader title="Recent conversations" onViewAll={recentConversations.length ? () => navigate('/sessions') : null} />
      {loading ? (
        <Stack spacing={1.5}><Skeleton variant="rounded" height={70} /><Skeleton variant="rounded" height={70} /></Stack>
      ) : recentConversations.length ? (
        <Stack spacing={1.5}>
          {recentConversations.map((s) => (
            <Paper key={s.session_id} variant="outlined"
              sx={{ p: isMobile ? 1.5 : 2, borderRadius: 2, cursor: 'pointer', transition: 'all .2s',
                '&:hover': { bgcolor: 'action.hover', transform: 'translateX(4px)', borderColor: 'primary.main' } }}
              onClick={() => navigate(`/session/${s.session_id}`)}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar sx={{ bgcolor: 'primary.light', width: 34, height: 34 }}><ChatIcon fontSize="small" /></Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={600} noWrap>{s.content_title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Conversation · {new Date(s.updated_at).toLocaleDateString()}
                    </Typography>
                  </Box>
                </Stack>
                <ArrowForwardIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <EmptyRow text="No conversations yet — start one to practise speaking." cta="Start a conversation" onClick={() => navigate('/explore')} icon={<ChatIcon sx={{ fontSize: 40, color: 'text.disabled' }} />} />
      )}

      {/* Recent voyages */}
      <SectionHeader title="Recent voyages" onViewAll={recentPaths.length ? () => navigate('/breadcrumbs') : null} />
      {loading ? (
        <Stack spacing={1.5}><Skeleton variant="rounded" height={70} /></Stack>
      ) : recentPaths.length ? (
        <Stack spacing={1.5}>
          {recentPaths.map((path) => (
            <Paper key={path.path_id} variant="outlined"
              sx={{ p: isMobile ? 1.5 : 2, borderRadius: 2, cursor: 'pointer', transition: 'all .2s',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' } }}
              onClick={() => navigate(`/breadcrumbs/paths/${path.path_id}`)}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={600} noWrap>{path.title}</Typography>
                  <Stack direction="row" spacing={1} mt={0.5}>
                    <Chip label={path.level} size="small" color="primary" sx={{ height: 20 }} />
                    <Chip label={`${path.stories_count} stories`} size="small" variant="outlined" sx={{ height: 20 }} />
                  </Stack>
                </Box>
                <Stack alignItems="center" sx={{ flexShrink: 0 }}>
                  <CircularProgress variant="determinate" value={path.completion_percentage || 0} size={38} thickness={4} />
                  <Typography variant="caption" color="text.secondary" mt={0.5}>{Math.round(path.completion_percentage || 0)}%</Typography>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <EmptyRow text="No voyages started yet — pick a story path to begin." cta="Browse voyages" onClick={() => navigate('/breadcrumbs')} icon={<PathsIcon sx={{ fontSize: 40, color: 'text.disabled' }} />} />
      )}

      {/* Recent journal entries */}
      <SectionHeader title="Recent journal entries" onViewAll={recentJournal.length ? () => navigate('/journal') : null} />
      {loading ? (
        <Stack spacing={1.5}><Skeleton variant="rounded" height={70} /></Stack>
      ) : recentJournal.length ? (
        <Stack spacing={1.5}>
          {recentJournal.map((entry) => (
            <Paper key={entry.entry_id} variant="outlined"
              sx={{ p: isMobile ? 1.5 : 2, borderRadius: 2, cursor: 'pointer', transition: 'all .2s',
                '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' } }}
              onClick={() => navigate('/journal')}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar sx={{ bgcolor: 'warning.light', width: 34, height: 34 }}><JournalIcon fontSize="small" /></Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant={isMobile ? 'body2' : 'subtitle1'} fontWeight={600} noWrap>{entry.title || 'Untitled'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(entry.created_at).toLocaleDateString()}
                      {entry.words_learned ? ` · ${entry.words_learned} words learned` : ''}
                    </Typography>
                  </Box>
                </Stack>
                <ArrowForwardIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <EmptyRow text="No journal entries yet — write about your day in your target language." cta="Write an entry" onClick={() => navigate('/journal')} icon={<JournalIcon sx={{ fontSize: 40, color: 'text.disabled' }} />} />
      )}

      {/* Progress summary (full Chart lives at /progress) */}
      <SectionHeader title="Progress" onViewAll={() => navigate('/progress')} />
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <CardActionArea onClick={() => navigate('/progress')}>
          <CardContent>
            <Grid container spacing={1} sx={{ textAlign: 'center' }}>
              <ProgressStat value={pv.total || 0} label="Words" color="primary.main" />
              <ProgressStat value={pv.due_count || 0} label="Due" color="warning.main" />
              <ProgressStat value={ps.stories_completed || 0} label="Stories" color="success.main" />
              <ProgressStat value={pa.current_streak || 0} label="Day streak" color="secondary.dark" icon={<StreakIcon fontSize="small" />} />
            </Grid>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 1.5, color: 'primary.main' }}>
              <MapIcon fontSize="small" />
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Open your full Chart</Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Box>
  );
};

function ProgressStat({ value, label, color, icon }) {
  return (
    <Grid item xs={3}>
      <Stack alignItems="center" spacing={0.25}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {icon && <Box sx={{ color }}>{icon}</Box>}
          <Typography variant="h5" sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, color }}>{value}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Stack>
    </Grid>
  );
}

export default HomePage;
